import { HttpStatusCode } from '@angular/common/http';
import {
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  signal,
} from '@angular/core';

import { Avatar, UserGoogleProfile } from '@iw/core/entities';
import { UserStore } from '@iw/users';

import { AuthStore } from '../auth-store';

export type ProfileType = 'user' | 'treasurer';
const ACTIVE_PROFILE_KEY = 'activeProfile';
const DEFAULT_PROFILE: ProfileType = 'user';
const TREASURER_AVATAR: Avatar = {
  smallUrl: '/assets/images/treasurer.png',
  bigUrl: '/assets/images/treasurer.png',
};

@Injectable({ providedIn: 'root' })
export class ProfileService {
  readonly #authStore = inject(AuthStore);
  readonly #injector = inject(Injector);
  #userStore: InstanceType<typeof UserStore> | null = null;

  readonly #activeProfile = signal<ProfileType>(DEFAULT_PROFILE);
  readonly #profile = signal<UserGoogleProfile | null>(null);
  readonly #initialized = signal<boolean>(false);
  readonly #getMeFailed = signal<boolean>(false);

  readonly me = computed(() => this.#user.user());

  readonly isSuperUser = computed(() => {
    const profile = this.#profile();
    return profile?.role?.includes('superuser') ?? false;
  });

  readonly isActiveTreasurer = computed(() => {
    return this.#activeProfile() === 'treasurer';
  });

  readonly displayAvatar = computed<Avatar>(() => {
    return this.#activeProfile() === 'treasurer'
      ? TREASURER_AVATAR
      : this.#profile()?.avatar;
  });

  readonly displayCaption = computed<string>(() => {
    return this.#activeProfile() === 'treasurer'
      ? 'userCardSmall.btn.treasurer'
      : this.#profile()?.caption;
  });

  readonly displayFirstLetter = computed<string>(() => {
    return this.#activeProfile() === 'treasurer'
      ? 'T'
      : (this.#profile()?.caption.at(0) ?? '');
  });

  readonly activeProfile = this.#activeProfile.asReadonly();
  readonly profile = this.#profile.asReadonly();
  readonly profileChange = signal<ProfileType>(null); // to refetch smth in effects on profile change

  readonly treasurerAvatar: Avatar = TREASURER_AVATAR;

  readonly isAuthorizedEffect = effect(() => {
    const isAuthorized = this.#authStore.isAuthorized();
    const hasUser = !!this.#user.user();
    const isGetMePending = this.#user.isGetMePending();
    const getMeFailed = this.#getMeFailed();

    if (isAuthorized) {
      if (!hasUser && !isGetMePending && !getMeFailed) {
        this.#user.getMe();
      }
    } else {
      this.#clearAll();
    }
  });

  /** Effect to monitor user data and update profile */
  readonly userDataEffect = effect(() => {
    const user = this.#user.user();
    const isAuthorized = this.#authStore.isAuthorized();

    if (isAuthorized && user) {
      this.#profile.set(this.#buildProfile());
      this.#initializeActiveProfile();
    }
  });

  /** Effect to monitor getMe and update auth status */
  readonly getMeEffect = effect(() => {
    const getMeSuccess = this.#user.isGetMeSuccess();
    const getMeError = this.#user.getMeError();

    if (getMeSuccess !== null) {
      const me = this.#user.user();
      const isValid = !!me?.id;
      this.#authStore.setAuthStatus(isValid);

      if (isValid) {
        this.#getMeFailed.set(false);
      } else {
        this.#getMeFailed.set(true);
      }
    }

    if (getMeError) {
      const errorStatus = getMeError.originalError?.status;
      if (
        errorStatus === HttpStatusCode.BadRequest ||
        errorStatus === HttpStatusCode.Unauthorized ||
        errorStatus === HttpStatusCode.Forbidden
      ) {
        this.#getMeFailed.set(true);
        this.#authStore.setAuthStatus(false);
      }
    }
  });

  get #user(): InstanceType<typeof UserStore> {
    if (!this.#userStore) {
      this.#userStore = this.#injector.get(UserStore);
    }
    return this.#userStore;
  }

  /** Initialize authentication by fetching user profile */
  initializeAuth(): void {
    const hasUser = !!this.#user.user();
    const isGetMePending = this.#user.isGetMePending();
    const getMeFailed = this.#getMeFailed();

    if (hasUser || isGetMePending || getMeFailed || this.#initialized()) return;

    this.#initialized.set(true);
    this.#user.getMe();
  }

  /** Set active profile and persist to storage */
  setActiveProfile(profile: ProfileType): void {
    if (!this.#canSetProfile(profile)) return;
    this.#activeProfile.set(profile);
    localStorage.setItem(ACTIVE_PROFILE_KEY, profile);
    this.profileChange.set(profile);
  }

  /** Initialize active profile from storage or default */
  #initializeActiveProfile(): void {
    const saved = localStorage.getItem(ACTIVE_PROFILE_KEY);
    if (this.#canSetProfile(saved)) {
      this.#activeProfile.set(saved);
    } else {
      this.#activeProfile.set(DEFAULT_PROFILE);
      localStorage.setItem(ACTIVE_PROFILE_KEY, DEFAULT_PROFILE);
    }
  }

  /** Type guard for profile values */
  #isValidProfile(profile: string | null): profile is ProfileType {
    return profile === 'user' || profile === 'treasurer';
  }

  /** Check if profile can be set */
  #canSetProfile(profile: string | null): profile is ProfileType {
    return this.#isValidProfile(profile) && this.isSuperUser();
  }

  /** Extract and normalize roles from groups */
  #extractRoles(groups: string[] = []): string[] {
    if (!groups.length) return ['user'];
    return Array.from(
      new Set(
        groups
          .map((group) => group.split('.').pop()?.toLowerCase() || 'user')
          .filter(Boolean),
      ),
    );
  }

  /** Build user profile from JWT token */
  #buildProfile(): UserGoogleProfile | null {
    const meData = this.me();
    if (!meData) return null;

    const { firstName, lastName, email, avatar, roles } = meData;
    const role = this.#extractRoles(roles);
    return {
      name: firstName ?? '',
      surname: lastName ?? '',
      caption: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
      role: role,
      avatar: {
        smallUrl: avatar ?? '',
        bigUrl: avatar ?? '',
      } as Avatar,
      email: email ?? '',
      isSuperuser: role.includes('superuser'),
    };
  }

  /** Clear all data after logout */
  #clearAll(): void {
    this.#profile.set(null);
    this.#activeProfile.set(null);
    this.profileChange.set(null);
    this.#initialized.set(false);
  }
}
